import React from 'react';
import { Modal, ConfigProvider, theme as antTheme } from 'antd';
import { Upload as UploadIcon, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiInstance as api } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { PRESET_AVATARS } from '../../constants/avatars';

// Shared avatar picker: pick one of the preset icons, or upload your own
// photo. Used from the account menu and from Settings — both just render
// <AvatarPicker isOpen onClose />, everything else (saving, syncing the
// logged-in user) lives here once.
export function AvatarPicker({ isOpen, onClose }) {
  const { user, updateProfile } = useAuth();
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const fileInputRef = React.useRef(null);
  const [saving, setSaving] = React.useState(null); // preset path being saved, or 'upload'

  async function choosePreset(avatarPath) {
    if (saving) return;
    setSaving(avatarPath);
    const { success } = await updateProfile({ avatar: avatarPath });
    setSaving(null);
    if (success) onClose();
  }

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setSaving('upload');
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      const res = await api.post('/api/v1/users/me/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const avatarUrl = res.data?.data?.avatar;
      const { success } = await updateProfile({ avatar: avatarUrl });
      if (success) onClose();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to upload photo');
    } finally {
      setSaving(null);
    }
  }

  const antConfig = {
    algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
    token: {
      colorPrimary: '#3b82f6',
      borderRadius: 8,
      colorBgContainer: isDark ? '#1a1d2e' : '#ffffff',
      colorBgElevated: isDark ? '#1a1d2e' : '#ffffff',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
  };

  return (
    <ConfigProvider theme={antConfig}>
      <Modal
        open={isOpen}
        onCancel={onClose}
        title="Choose your avatar"
        footer={null}
        width={480}
        destroyOnClose
      >
        <p style={{ fontSize: 13, color: isDark ? 'rgba(255,255,255,0.55)' : '#64748b', marginTop: -4, marginBottom: 16 }}>
          Pick an icon, or upload your own photo.
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: 10,
            maxHeight: 320,
            overflowY: 'auto',
            paddingRight: 4,
            marginBottom: 16,
          }}
        >
          {PRESET_AVATARS.map((src) => {
            const active = user?.avatar === src;
            const busy = saving === src;
            return (
              <button
                key={src}
                type="button"
                onClick={() => choosePreset(src)}
                disabled={!!saving}
                title="Use this avatar"
                style={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '1',
                  borderRadius: 10,
                  border: active ? '2px solid #3b82f6' : '1px solid rgba(128,128,128,0.25)',
                  background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc',
                  padding: 4,
                  cursor: saving ? 'default' : 'pointer',
                  opacity: busy ? 0.5 : 1,
                }}
              >
                <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                {active && (
                  <span style={{
                    position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: 99,
                    background: '#3b82f6', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Check size={11} strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={!!saving}
          style={{
            width: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            height: 38, borderRadius: 8, border: '1px dashed rgba(128,128,128,0.4)',
            background: 'transparent', color: isDark ? '#fff' : '#0f172a',
            fontSize: 13, fontWeight: 500, cursor: saving ? 'default' : 'pointer',
            opacity: saving === 'upload' ? 0.6 : 1,
          }}
        >
          <UploadIcon size={15} />
          {saving === 'upload' ? 'Uploading…' : 'Upload your own photo'}
        </button>
      </Modal>
    </ConfigProvider>
  );
}
